import { useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { ChatComposer } from '@/modules/hiring-manager/chat/chatComposer/ChatComposer';
import {
  hasPendingReply,
  startingTurn,
} from '@/modules/hiring-manager/chat/chatMessages';
import { ChatPanel } from '@/modules/hiring-manager/chat/chatPanel/ChatPanel';
import { useIsPanelBeside } from '@/modules/hiring-manager/chat/chatPanel/useIsPanelBeside';
import {
  confirmationOutcomes,
  latestCandidateCard,
  latestEditorRequest,
  latestJobResult,
  newestCompletedReply,
  savedAgainAfter,
} from '@/modules/hiring-manager/chat/chatResults';
import { ChatThread } from '@/modules/hiring-manager/chat/chatThread/ChatThread';
import type { ChatCandidate } from '@/modules/hiring-manager/chat/replyResults/ReplyResults';
import type {
  ChatApi,
  ChatMessageApi,
} from '@/modules/hiring-manager/chat/types';
import { useChatDecide } from '@/modules/hiring-manager/chat/useChatDecide';
import { useChatQuery } from '@/modules/hiring-manager/chat/useChatQuery';
import { useChatSend } from '@/modules/hiring-manager/chat/useChatSend';
import { useChatStartOnOpen } from '@/modules/hiring-manager/chat/useChatStartOnOpen';
import {
  chatOpened,
  useNewChat,
} from '@/modules/hiring-manager/chat/useNewChat';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import Loader from '@/modules/ui/components/loader/Loader';
import { CandidatePanel } from '@/pages/hm/job-candidates/CandidatePanel';

import { ChatScreen } from './ChatScreen';

interface ChatConversationProps {
  chatId: string;
}

/**
 * `/hm/chat/:conversationId`: one of the user's chats. A new chat opens here before it exists: the page sends its first
 * message and shows it, with a pending reply, until finlay-api answers with the chat.
 */
export function ChatConversation({ chatId }: ChatConversationProps) {
  const { starting, failure } = useChatStartOnOpen(chatId);
  // Nothing to load until the first message is answered: before finlay-api saves the chat, it isn't found.
  const chatQuery = useChatQuery(starting === null ? chatId : undefined);
  useLayoutEffect(chatOpened, []);

  if (starting !== null) {
    return (
      <OpenChat
        chat={{ id: chatId, title: null, messages: startingTurn(starting, '') }}
      />
    );
  }
  if (chatQuery.isPending) return <Loader />;
  // A check that failed while the chat is shown keeps the copy on screen; the next check recovers by itself.
  if (chatQuery.data) return <OpenChat chat={chatQuery.data} />;
  // The first message failed and the chat was never made: it goes back in the box, to try again.
  if (failure) return <StartFailed failure={failure} />;
  return (
    <div className="mx-auto w-full max-w-[760px] px-4 pt-10 md:px-10">
      <HMLoadError
        description="We could not load this chat. It may have been deleted, or your connection dropped."
        onRetry={() => void chatQuery.refetch()}
        title="Chat unavailable"
      />
    </div>
  );
}

function StartFailed({
  failure,
}: {
  failure: NonNullable<ReturnType<typeof useChatStartOnOpen>['failure']>;
}) {
  const openNewChat = useNewChat();

  return (
    <ChatScreen
      footer={
        <>
          <InlineError>{failure.message}</InlineError>
          <ChatComposer
            busy={false}
            defaultValue={failure.text}
            onSend={(text) => openNewChat(text, failure.contextJobId)}
          />
        </>
      }
    >
      {null}
    </ChatScreen>
  );
}

const isReply = (message: ChatMessageApi) => message.role === 'Assistant';

function OpenChat({ chat }: { chat: ChatApi }) {
  const isBeside = useIsPanelBeside();
  const { plan } = useWorkspacePlan();
  const { send, sending, sendingText, failure } = useChatSend(chat);
  const { decide, deciding, failure: decideFailure } = useChatDecide(chat);
  const [searchParams] = useSearchParams();
  // Replies already finished when the chat opened are history; any other one arrived while it was open and appears
  // as if streamed. A new chat opens with its first reply pending, so that reply streams in too.
  const [history] = useState(
    () =>
      new Set(
        chat.messages
          .filter(
            (message) => isReply(message) && message.status === 'Completed',
          )
          .map((message) => message.id),
      ),
  );
  // Messages sent or answered while the chat is open fade in, from the turn still being answered when it opened, as
  // on a chat just started; earlier ones are history and show at once.
  const [enterFrom] = useState(() => {
    const pending = chat.messages.findIndex(
      (message) => isReply(message) && message.status === 'Pending',
    );
    return pending === -1 ? chat.messages.length : Math.max(0, pending - 1);
  });
  const freshReplyIds = new Set(
    chat.messages
      .filter((message) => isReply(message) && !history.has(message.id))
      .map((message) => message.id),
  );
  // One turn at a time: a message, or a click on a change, waits until the reply being written is in.
  const busy = sending || deciding !== undefined || hasPendingReply(chat);

  // The panel shows the chat's job, so a chat without one has no panel. From `lg` it sits beside the chat; below that
  // it covers the page, so it opens when the HM asks, and by itself for a job summary that just arrived: the HM sees
  // the job before replying to it. Closing it is the HM's choice for that job.
  const latest = latestJobResult(chat.messages);
  const latestId = latest?.messageId ?? null;
  const [panelChoice, setPanelChoice] = useState<{
    messageId: string | null;
    open: boolean;
  } | null>(null);
  const panelOpen =
    latest !== null &&
    (panelChoice?.messageId === latestId
      ? panelChoice.open
      : isBeside ||
        (latest.result.type === 'job_summary' &&
          freshReplyIds.has(latest.messageId)));

  // A reply that saved the job for the HM to edit themselves opens the editor when it arrives while the chat is open;
  // reopened later, the chat offers it from the saved job instead. The editor then stays open, whatever replies
  // follow, until the HM closes or saves it. On the Managed plan the HM's Finlay recruiter makes the changes, as on
  // the edit page, so there is no editor.
  const editorRequest =
    plan === 'managed' ? null : latestEditorRequest(chat.messages);
  const [editorChoice, setEditorChoice] = useState<{
    messageId: string;
    open: boolean;
  } | null>(null);
  const editing =
    editorRequest !== null &&
    (editorChoice?.messageId === editorRequest.messageId
      ? editorChoice.open
      : freshReplyIds.has(editorRequest.messageId));
  const chooseEditor = (open: boolean) =>
    editorRequest &&
    setEditorChoice({ messageId: editorRequest.messageId, open });
  // A newer reply that saved the same job: the editor still shows the job as it was, so it offers to read it again.
  const [editorReload, setEditorReload] = useState<{
    requestId: string;
    afterId: string;
    version: number;
  } | null>(null);
  const reload =
    editorReload?.requestId === editorRequest?.messageId ? editorReload : null;
  const editorStale =
    editing &&
    savedAgainAfter(
      chat.messages,
      editorRequest.jobId,
      reload?.afterId ?? editorRequest.messageId,
    );
  const reloadEditor = () => {
    const newestReply = newestCompletedReply(chat.messages);
    if (editorRequest && newestReply) {
      setEditorReload({
        requestId: editorRequest.messageId,
        afterId: newestReply.id,
        version: (reload?.version ?? 0) + 1,
      });
    }
  };

  // The candidate beside the chat: one the HM opened from a list or a candidate line, or the newest card when it
  // arrives while the chat is open beside it. As with the job, the HM's choice holds until a newer card arrives. The
  // editor keeps the panel while it's open, so its edits aren't lost: a candidate then opens in a new tab.
  const card = latestCandidateCard(chat.messages);
  const cardId = card?.messageId ?? null;
  const [candidateChoice, setCandidateChoice] = useState<{
    messageId: string | null;
    candidate: ChatCandidate | null;
  } | null>(null);
  const openCandidate =
    candidateChoice?.messageId === cardId
      ? candidateChoice.candidate
      : card && isBeside && freshReplyIds.has(card.messageId)
        ? {
            jobId: card.result.job.id,
            candidateId: card.result.candidateId,
            url: card.result.url,
          }
        : (candidateChoice?.candidate ?? null);
  const showCandidate = (candidate: ChatCandidate | null) => {
    if (candidate && editing) {
      if (candidate.url) window.open(candidate.url, '_blank', 'noopener');
      return;
    }
    setCandidateChoice({ messageId: cardId, candidate });
  };
  const candidateBeside = !editing && openCandidate !== null;

  return (
    <>
      <ChatScreen
        footer={
          <>
            {latest && !panelOpen && !editing && (
              <Button
                className="self-end"
                onClick={() =>
                  setPanelChoice({ messageId: latestId, open: true })
                }
                size="sm"
                variant="outline"
              >
                Show job
              </Button>
            )}
            {failure && <InlineError>{failure.message}</InlineError>}
            {decideFailure && <InlineError>{decideFailure}</InlineError>}
            <ChatComposer
              busy={busy}
              defaultValue={failure?.text}
              key={failure ? `failed-${failure.at}` : 'composer'}
              onSend={send}
            />
          </>
        }
      >
        <ChatThread
          enterFrom={enterFrom}
          freshReplyIds={freshReplyIds}
          messages={chat.messages}
          onRetry={send}
          results={{
            outcomes: confirmationOutcomes(chat.messages),
            onSend: send,
            onDecide: decide,
            onOpenCandidate: showCandidate,
            busy,
            sending: sendingText,
            deciding,
          }}
          retryDisabled={busy}
        />
      </ChatScreen>
      {candidateBeside && (
        <CandidatePanel
          candidateId={openCandidate.candidateId}
          jobId={openCandidate.jobId}
          key={openCandidate.candidateId}
          onClose={() => showCandidate(null)}
          onNavigate={() => {}}
          searchSuffix={toSearchSuffix(searchParams)}
          showFailureReason={false}
        />
      )}
      {!candidateBeside && (panelOpen || editing) && (
        <ChatPanel
          busy={busy}
          editingJobId={editing ? editorRequest.jobId : undefined}
          editorStale={editorStale}
          editorVersion={reload?.version ?? 0}
          messages={chat.messages}
          onClose={() => {
            setPanelChoice({ messageId: latestId, open: false });
            if (editing) chooseEditor(false);
          }}
          onEdit={
            editorRequest &&
            latest?.result.type === 'job_saved' &&
            latest.result.jobId === editorRequest.jobId
              ? () => chooseEditor(true)
              : undefined
          }
          onEditorDone={() => chooseEditor(false)}
          onReloadEditor={reloadEditor}
          onSend={send}
          sending={sendingText}
        />
      )}
    </>
  );
}
